/*  Dip-to-Dip — handbook §6 (Fuel Dip Shortage Handling)
    Chandrakant dips every underground tank at 7:00 AM daily. This page lets
    him log those dips with photo proof and compares physical vs computed.

    Handbook rule: opening + purchases − sales = expected closing. Actual
    closing (dip-stick reading) may differ by ±50–100 L due to oval tanks &
    temperature. Anything larger self-flags a SHORTAGE_TIER red flag. */
import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { fmtDateDMY, fmtNum, todayISO } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast, Toaster } from "sonner";
import { Camera, Check, Info, Droplet } from "lucide-react";

// Handbook §"Attendee Details" — dips are usually done at start of morning
// shift (7 AM); some pumps also dip at shift changes. Show operator-friendly
// labels rather than SC-01/SC-02/SC-03 codes.
const DIP_MOMENTS = [
  { code: "SC-01", label: "Morning (7 AM opening)" },
  { code: "SC-02", label: "Evening (3 PM change-over)" },
  { code: "SC-03", label: "Night (11 PM change-over)" },
];

const TANK_META = {
  HSD: { label: "Diesel (HSD)", tint: "text-amber-800" },
  MS:  { label: "Petrol (MS)",  tint: "text-emerald-800" },
  CNG: { label: "CNG",          tint: "text-sky-800" },
};

export default function DipToDip() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState([]);
  const [from, setFrom] = useState(new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10));
  const [to, setTo] = useState(todayISO());
  const [form, setForm] = useState({
    date: todayISO(),
    shift_code: "SC-01",
    tank: "HSD",
    opening_dip_l: 0,
    purchases_l: 0,
    closing_dip_l: 0,
    notes: "",
    proof_file_id: null,
    proof_name: "",
    // Phase-3 B3: photo of the paper book page (audit trail vs. typed value).
    book_photo_file_id: null,
    book_photo_name: "",
  });
  const [uploadingProof, setUploadingProof] = useState(false);
  const [uploadingBook, setUploadingBook] = useState(false);
  const [lastClosingByTank, setLastClosingByTank] = useState({});  // used to prefill opening

  const load = async () => {
    try {
      const [l, s] = await Promise.all([
        api.get("/dips/", { params: { date_from: from, date_to: to } }),
        api.get("/dips/summary", { params: { date_from: from, date_to: to } }),
      ]);
      setRows(l.data);
      setSummary(s.data);
      // Build per-tank last-known-closing map — used to prefill "Opening" on
      // the next dip so the operator doesn't retype.
      const seen = {};
      for (const r of l.data) {
        if (!seen[r.tank] && r.closing_dip_l != null) seen[r.tank] = r.closing_dip_l;
      }
      setLastClosingByTank(seen);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [from, to]); // eslint-disable-line

  // When operator switches tank, prefill the opening from last known closing.
  useEffect(() => {
    if (lastClosingByTank[form.tank] != null && !form.opening_dip_l) {
      setForm((s) => ({ ...s, opening_dip_l: lastClosingByTank[s.tank] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.tank, lastClosingByTank]);

  // Live preview — expected closing and computed shortage. Fetched from the
  // server on blur/submit, but a quick client-side estimate helps the operator
  // catch typos IMMEDIATELY.
  const preview = useMemo(() => {
    const open = Number(form.opening_dip_l) || 0;
    const purch = Number(form.purchases_l) || 0;
    const close = Number(form.closing_dip_l) || 0;
    // Sales isn't known here (comes from shift entries) — the server fills it.
    // But we can still show expected = open + purch and let operator compare.
    const expectedIgnoringSales = open + purch;
    const gap = expectedIgnoringSales - close;
    return { open, purch, close, expectedIgnoringSales, gap };
  }, [form.opening_dip_l, form.purchases_l, form.closing_dip_l]);

  const save = async () => {
    if (!form.opening_dip_l || !form.closing_dip_l) {
      toast.error("Fill both the start-of-day dip and the end-of-day dip (in litres).");
      return;
    }
    try {
      const payload = { ...form };
      delete payload.proof_name;
      delete payload.book_photo_name;
      await api.post("/dips/", payload);
      toast.success("Saved. Shortage will auto-compute against today's shift sales.");
      setForm((s) => ({ ...s, opening_dip_l: 0, closing_dip_l: 0, purchases_l: 0, notes: "", proof_file_id: null, proof_name: "", book_photo_file_id: null, book_photo_name: "" }));
      load();
    }
    catch (e) { toast.error(apiError(e)); }
  };

  const uploadProof = async (file) => {
    setUploadingProof(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (form.date) fd.append("date", form.date);
      if (form.shift_code) fd.append("shift_code", form.shift_code);
      fd.append("note", `Dip photo — ${form.tank} ${form.date} ${form.shift_code}`);
      const r = await api.post("/uploads/", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, proof_file_id: r.data.file_id, proof_name: r.data.original_name }));
      toast.success("Dip-stick photo attached");
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploadingProof(false); }
  };

  // Phase-3 B3 — separate upload for the paper-book page (Chandrakant's
  // register). Keeps it distinct from the dip-stick photo so both can be
  // stored and viewed independently on the entry detail.
  const uploadBookPhoto = async (file) => {
    setUploadingBook(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (form.date) fd.append("date", form.date);
      if (form.shift_code) fd.append("shift_code", form.shift_code);
      fd.append("note", `Dip book page — ${form.tank} ${form.date} ${form.shift_code}`);
      const r = await api.post("/uploads/", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, book_photo_file_id: r.data.file_id, book_photo_name: r.data.original_name }));
      toast.success("Handwritten-book photo attached");
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploadingBook(false); }
  };

  const meta = TANK_META[form.tank] || TANK_META.HSD;
  const gapClass = Math.abs(preview.gap) > 100 ? "text-red-700" : Math.abs(preview.gap) > 50 ? "text-amber-700" : "text-emerald-700";

  return (
    <div className="space-y-4" data-testid="dips-root">
      <Toaster position="top-center" />

      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Dip-to-Dip</h1>
        <p className="text-sm text-slate-600 mt-1">
          Physical dip-stick reading vs the computed inventory. Chandrakant dips every underground tank at 7 AM daily — enter each tank one at a time.
        </p>
        <div className="text-xs text-slate-700 bg-sky-50 border-2 border-sky-300 rounded-sm p-2 mt-2 flex gap-2 items-start">
          <Info size={14} className="shrink-0 mt-0.5 text-sky-700" />
          <div>
            <b>How the math works:</b> Opening dip + Fuel received via tanker − Fuel sold by DUs = Expected closing dip.<br />
            Variations of ±50–100 L are normal (oval tanks, temperature). Above that, we auto-flag a shortage.
          </div>
        </div>
      </div>

      <Card className="p-4 border-2 border-slate-900 rounded-sm bg-slate-50">
        <div className="flex items-center gap-2 mb-3">
          <Droplet size={18} className={meta.tint} />
          <h3 className="font-bold text-slate-900">Log today&rsquo;s dip — {meta.label}</h3>
        </div>

        {/* Row 1: Which day / moment / tank */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Date</Label>
            <Input data-testid="dip-date" type="date" value={form.date}
                   onChange={(e) => setForm({ ...form, date: e.target.value })}
                   className="h-11 mt-1 border border-slate-300 bg-white" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Moment of dip</Label>
            <select data-testid="dip-moment" value={form.shift_code}
                    onChange={(e) => setForm({ ...form, shift_code: e.target.value })}
                    className="w-full h-11 mt-1 border border-slate-300 rounded-sm px-2 bg-white text-sm">
              {DIP_MOMENTS.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Tank</Label>
            <div className="flex gap-1 mt-1">
              {Object.keys(TANK_META).map((t) => (
                <button key={t} type="button" onClick={() => setForm({ ...form, tank: t, opening_dip_l: 0 })}
                        data-testid={`dip-tank-${t}`}
                        className={"flex-1 h-11 border-2 font-bold text-sm rounded-sm " +
                          (form.tank === t ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300")}>
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Row 2: The three number inputs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          <div className="bg-white p-3 border-2 border-slate-300 rounded-sm">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
              1. Opening dip (start of day) — litres
            </Label>
            <Input data-testid="dip-opening" type="number" step="0.01" value={form.opening_dip_l || ""}
                   onChange={(e) => setForm({ ...form, opening_dip_l: Number(e.target.value) })}
                   placeholder="e.g. 14000"
                   className="h-12 mt-1 num text-lg font-bold border border-slate-300" />
            {lastClosingByTank[form.tank] != null && (
              <div className="text-[11px] text-slate-500 mt-1">Prefilled from previous closing dip: <b>{fmtNum(lastClosingByTank[form.tank])} L</b> — override if you took a fresh reading.</div>
            )}
          </div>

          <div className="bg-white p-3 border-2 border-slate-300 rounded-sm">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
              2. Fuel received via tanker today — litres
            </Label>
            <Input data-testid="dip-purchases" type="number" step="0.01" value={form.purchases_l || ""}
                   onChange={(e) => setForm({ ...form, purchases_l: Number(e.target.value) })}
                   placeholder="0 if no delivery today"
                   className="h-12 mt-1 num text-lg font-bold border border-slate-300" />
            <div className="text-[11px] text-slate-500 mt-1">Leave 0 if no tanker came. Cross-check with the TT Receipt in Fuel Purchase.</div>
          </div>

          <div className="bg-white p-3 border-2 border-slate-300 rounded-sm">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
              3. Closing dip (end of day) — litres
            </Label>
            <Input data-testid="dip-closing" type="number" step="0.01" value={form.closing_dip_l || ""}
                   onChange={(e) => setForm({ ...form, closing_dip_l: Number(e.target.value) })}
                   placeholder="e.g. 19900"
                   className="h-12 mt-1 num text-lg font-bold border border-slate-300" />
            <div className="text-[11px] text-slate-500 mt-1">The dip-stick reading you took just now.</div>
          </div>
        </div>

        {/* Live preview badge */}
        {(preview.open > 0 || preview.close > 0) && (
          <div className="mt-3 p-3 border-2 border-slate-400 bg-white rounded-sm flex flex-wrap gap-4 items-center" data-testid="dip-preview">
            <div>
              <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Available inventory today</div>
              <div className="num text-lg font-bold text-slate-900">{fmtNum(preview.expectedIgnoringSales)} L</div>
              <div className="text-[10px] text-slate-400">= opening + tanker</div>
            </div>
            <div className="text-slate-400 text-xl">−</div>
            <div>
              <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Closing dip</div>
              <div className="num text-lg font-bold text-slate-900">{fmtNum(preview.close)} L</div>
            </div>
            <div className="text-slate-400 text-xl">=</div>
            <div>
              <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Gap (before sales)</div>
              <div className={"num text-2xl font-bold " + gapClass}>{fmtNum(preview.gap)} L</div>
              <div className="text-[10px] text-slate-500">Sales for the day will be subtracted server-side. Real shortage = gap − sales.</div>
            </div>
          </div>
        )}

        {/* Notes + proof */}
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Notes (optional)</Label>
            <Input data-testid="dip-notes" placeholder="e.g. rain during dip, tanker part-delivery, water dip 0 cm"
                   value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                   className="h-11 mt-1 border border-slate-300" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Dip-stick photo</Label>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <label className="flex items-center gap-1 text-xs font-bold px-3 py-2 bg-white border-2 border-slate-300 rounded-sm cursor-pointer hover:bg-slate-50" data-testid="dip-photo-label">
                <input type="file" accept="image/*,application/pdf" capture="environment" className="hidden" data-testid="dip-photo-input"
                       onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadProof(f); }} />
                <Camera size={12} /> {uploadingProof ? "Uploading…" : (form.proof_file_id ? "Replace photo" : "Take dip photo")}
              </label>
              {form.proof_file_id && (
                <span className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-300 px-2 py-1 rounded-sm inline-flex items-center gap-1" data-testid="dip-photo-ok">
                  <Check size={10} /> {form.proof_name}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Phase-3 B3: paper-book photo — separate row so it doesn't get
            mistaken for the dip-stick photo. */}
        <div className="mt-3 bg-white p-3 border-2 border-dashed border-slate-400 rounded-sm">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
            Handwritten register page (mandatory)
          </Label>
          <div className="text-[11px] text-slate-500 mb-2">
            Take a clear photo of the page in the paper book where this dip
            was recorded. Any typed number here is checked back against the
            book by the manager.
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-1 text-xs font-bold px-3 py-2 bg-white border-2 border-slate-300 rounded-sm cursor-pointer hover:bg-slate-50" data-testid="dip-book-label">
              <input type="file" accept="image/*,application/pdf" capture="environment" className="hidden" data-testid="dip-book-input"
                     onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadBookPhoto(f); }} />
              <Camera size={12} /> {uploadingBook ? "Uploading…" : (form.book_photo_file_id ? "Replace book photo" : "Take book-page photo")}
            </label>
            {form.book_photo_file_id && (
              <span className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-300 px-2 py-1 rounded-sm inline-flex items-center gap-1" data-testid="dip-book-ok">
                <Check size={10} /> {form.book_photo_name}
              </span>
            )}
          </div>
        </div>

        <Button data-testid="save-dip-btn" className="mt-3 bg-slate-900 hover:bg-slate-800 text-white h-11 px-6" onClick={save}>
          Save dip reading
        </Button>
      </Card>

      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h3 className="font-bold text-slate-800">Summary by tank ({fmtDateDMY(from)} → {fmtDateDMY(to)})</h3>
          <div className="flex gap-2">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 border border-slate-300 rounded-sm px-2" />
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 border border-slate-300 rounded-sm px-2" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="dense-table w-full text-sm">
            <thead><tr><th>Tank</th><th>Sales (L)</th><th>Shortage (L)</th><th>Shortage %</th><th>Days w/ shortage</th></tr></thead>
            <tbody>
              {summary.map((s) => (
                <tr key={s.tank} className={s.shortage_pct > 0.4 ? "state-danger" : s.shortage_pct > 0.2 ? "state-warn" : ""}>
                  <td className="font-bold">{s.tank}</td>
                  <td className="num">{fmtNum(s.sales_l)}</td>
                  <td className={"num font-bold " + (s.shortage_l > 0 ? "text-red-600" : "text-emerald-700")}>{fmtNum(s.shortage_l)}</td>
                  <td className={"num font-bold " + (s.shortage_pct > 0.4 ? "text-red-600" : s.shortage_pct > 0.2 ? "text-amber-700" : "text-emerald-700")}>{s.shortage_pct}%</td>
                  <td className="num">{s.days_with_shortage}/{s.count}</td>
                </tr>
              ))}
              {summary.length === 0 && <tr><td colSpan={5} className="text-center text-slate-500 italic p-3">No dips logged in this range.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <h3 className="font-bold text-slate-800 mb-2">Dip reading history</h3>
        <div className="overflow-x-auto">
          <table className="dense-table w-full text-sm">
            <thead><tr><th>Date</th><th>Moment</th><th>Tank</th><th>Open</th><th>Tanker</th><th>Sales</th><th>Expected</th><th>Actual dip</th><th>Shortage</th><th>%</th></tr></thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.dip_id} className={d.shortage_pct > 0.4 ? "state-danger" : d.shortage_pct > 0.2 ? "state-warn" : ""}>
                  <td>{fmtDateDMY(d.date)}</td>
                  <td className="text-xs">{DIP_MOMENTS.find((m) => m.code === d.shift_code)?.label?.split(" ")[0] || d.shift_code}</td>
                  <td className="font-bold">{d.tank}</td>
                  <td className="num">{fmtNum(d.opening_dip_l)}</td>
                  <td className="num">{fmtNum(d.purchases_l)}</td>
                  <td className="num">{fmtNum(d.sales_l)}</td>
                  <td className="num">{fmtNum(d.computed_closing_l)}</td>
                  <td className="num">{fmtNum(d.closing_dip_l)}</td>
                  <td className={"num font-bold " + (d.shortage_l > 0 ? "text-red-600" : "text-emerald-700")}>{fmtNum(d.shortage_l)}</td>
                  <td className={"num font-bold " + (d.shortage_pct > 0.4 ? "text-red-600" : d.shortage_pct > 0.2 ? "text-amber-700" : "text-emerald-700")}>{d.shortage_pct}%</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={10} className="text-center text-slate-500 italic p-3">Nothing logged in this range yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
